import { useMemo, useState } from "react";

import {
    LendingVaultExpanded,
    RewardMint,
    useOraclePrices,
    UserExternalRewardsInfoExpanded
} from "@bridgesplit/abf-react";
import { MediaQuery, Row, ShadowCard, Text, ToggleChevron, Column, StatProps, TooltipText } from "@bridgesplit/ui";
import { Collapse, useMediaQuery } from "@mui/material";
import { AutoAwesomeOutlined } from "@mui/icons-material";
import { formatDate, formatUsd } from "@bridgesplit/utils";
import { VaultTagIcon } from "app/components/explore/vaults/VaultTitle";
import { PositionCardStats } from "app/components/common";

import { VaultActions } from "./VaultActionButtons";
import { LendVaultImage } from "./LendVaultImage";
import { VaultExternalRewards } from "./VaultExternalRewards";

export function UnclaimedPositionCard({
    query,
    vaultExpanded,
    rewards,
    lastWithdrawalTimestamp
}: {
    vaultExpanded: LendingVaultExpanded | undefined;
    query: MediaQuery;
    rewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined;
    lastWithdrawalTimestamp: number | undefined;
}) {
    const [expandedState, setExpanded] = useState(false);

    const isMobile = useMediaQuery(query.below);

    const expanded = expandedState || isMobile;

    const mints = useMemo(() => (rewards ? Object.keys(rewards) : []), [rewards]);
    const { getOracle } = useOraclePrices(mints);

    const unclaimedPositionValue = useMemo(() => {
        if (!rewards) return 0;
        return Object.values(rewards).reduce(
            (acc, reward) => acc + reward.pendingRewards * (getOracle(reward.rewardMint)?.usdPrice ?? 0),
            0
        );
    }, [rewards, getOracle]);

    const stats = getUnclaimedPositionStats(rewards, unclaimedPositionValue, lastWithdrawalTimestamp);

    return (
        <ShadowCard sx={{ flexGrow: 1, overflow: "visible" }} padding={false}>
            <Row
                sx={{ p: 2, cursor: rewards ? "pointer" : undefined }}
                onClick={rewards ? () => setExpanded((prev) => !prev) : undefined}
                spaceBetween
            >
                <Row spacing={1}>
                    <LendVaultImage lendVaultExpanded={vaultExpanded} size="md" />
                    <VaultTagIcon tags={vaultExpanded?.vaultMetadata?.tags} />
                    <Text loadingWidth="100px" loading={!rewards}>
                        {vaultExpanded?.vaultMetadata?.name}
                    </Text>
                    <Text loadingWidth="55px" loading={!rewards} color="caption" translate="no">
                        {formatUsd(unclaimedPositionValue)}
                    </Text>
                    <UnclaimedRewardsTag />
                </Row>

                <Row sx={{ [query.below]: { display: "none" } }} spacing={1}>
                    <ToggleChevron textVariant="h2" expanded={expanded} />
                </Row>
            </Row>

            <Collapse in={expanded}>
                <Column sx={{ p: 2, pt: 0 }} spacing={2}>
                    <PositionCardStats stats={stats} forceColumn={!isMobile} loading={!rewards} />
                    <VaultActions
                        vaultPosition={undefined}
                        vaultExpanded={vaultExpanded}
                        hasUnclaimedVaultRewards={!!rewards}
                        isCondensed={isMobile}
                    />
                </Column>
            </Collapse>
        </ShadowCard>
    );
}

function UnclaimedRewardsTag() {
    return (
        <TooltipText
            icon={false}
            helpText={"Unclaimed rewards on withdrawn position"}
            sx={{ gap: 0.5 }}
            variant={"body1"}
            color={"caption"}
        >
            {<AutoAwesomeOutlined />}
        </TooltipText>
    );
}

function getUnclaimedPositionStats(
    rewards: Record<RewardMint, UserExternalRewardsInfoExpanded> | undefined,
    unclaimedPositionValue: number,
    lastWithdrawalTimestamp: number | undefined
) {
    const stats: StatProps[] = [
        {
            value: formatUsd(unclaimedPositionValue),
            caption: "Unclaimed value",
            hide: !rewards
        },
        {
            caption: "Withdrawal date",
            value: formatDate(lastWithdrawalTimestamp)
        },
        {
            value: <VaultExternalRewards externalRewards={rewards} />,
            caption: "External rewards",
            hide: !rewards
        }
    ].filter((stat) => !stat.hide);

    return stats;
}
